import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  apiProductPackageProductDetailByLocationPackageIdFormatGetCollectionOptions,
  apiProductPackagesIdFormatGetOptions,
} from "@/generated/api/@tanstack/react-query.gen.ts";
import { Skeleton } from "@/components/ui/skeleton";
import ContainerInset from "@/components/container-inset.tsx";
import { PageErrorNoData } from "@/components/page-error.tsx";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout.tsx";
import PackageFormSkeleton from "@/app/(authenticated)/$merchantId/packages/_component/package-form-skeleton.tsx";
import PackageForm from "@/app/(authenticated)/$merchantId/packages/_component/package-form.tsx";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider.tsx";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/packages/$id/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { selectedMerchant } = useMerchantProvider();

  const packageQueryOptions = apiProductPackagesIdFormatGetOptions({
    headers: {
      "X-Merchant-Account-Id": selectedMerchant?.merchAccountId ?? "unknown",
    },
    path: { id },
  });

  const packageQuery = useQuery({
    ...packageQueryOptions,
    placeholderData: keepPreviousData,
  });

  const packageProductsQuery = useQuery({
    ...apiProductPackageProductDetailByLocationPackageIdFormatGetCollectionOptions(
      {
        headers: {
          "X-Merchant-Account-Id":
            selectedMerchant?.merchAccountId ?? "unknown",
        },
        path: {
          package_id: packageQuery.data?.packageId ?? "",
        },
        query: {
          page: 1,
          itemsPerPage: 999,
        },
      }
    ),
    enabled:
      !!packageQuery.data?.packageId && !!selectedMerchant?.merchAccountId,
    placeholderData: keepPreviousData,
  });

  const defaultValues = useMemo(() => {
    return {
      name: packageQuery.data?.name ?? "",
      sku: packageQuery.data?.sku ?? "",
      location: packageQuery.data?.location ?? "",
      productDetailsByLocation:
        packageProductsQuery.data?.member.map((item) => ({
          productDetailByLocation: item.productDetailByLocation?.["@id"] ?? "",
          quantity: item.quantity ? parseFloat(item.quantity).toString() : "0",
        })) ?? [],
    };
  }, [packageQuery.data, packageProductsQuery.data]);

  if (packageQuery.isPending || packageProductsQuery.isPending) {
    return (
      <ContainerInset
        breadcrumbs={[
          {
            title: "Packages",
            href: `/${selectedMerchant?.merchAccountId}/packages`,
          },
          {
            title: "Detail",
            href: "",
          },
        ]}
      >
        <PageLayoutRoot>
          <PageLayoutHeader
            title={<>Edit Package</>}
            tag={<Skeleton className="h-5 w-24" />}
          />
          <PageLayoutContent>
            <PackageFormSkeleton />
          </PageLayoutContent>
        </PageLayoutRoot>
      </ContainerInset>
    );
  }

  if (packageQuery.error || packageProductsQuery.error) {
    return <PageErrorNoData />;
  }

  if (!packageQuery.data || !packageQuery.data) {
    return <PageErrorNoData />;
  }

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Packages",
          href: `/${selectedMerchant?.merchAccountId}/packages`,
        },
        {
          title: "Detail",
          href: "",
        },
      ]}
    >
      <PageLayoutRoot>
        {/* Page Header */}
        <PageLayoutHeader
          title={<>Edit Package</>}
          tag={`SKU: ${packageQuery.data?.sku ?? "-"}`}
        />

        <PageLayoutContent>
          <PackageForm
            id={packageQuery.data?.packageId}
            onUpdateSuccess={() => {
              packageQuery.refetch();
              packageProductsQuery.refetch();
            }}
            defaultValues={defaultValues}
          />
        </PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}
